import { getTrfPdfConfigFields } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useLaunchMode } from '@/features/auth/launchContext'
import { logger } from '@/sentry.config'
import { useAppSelector } from '@/store/hooks'
import { warnClientSideTrfRender } from '@/utils/clientTrfRenderWarning'
import { extractFormOrderData } from '@/utils/FormDataExtraction'
import { openBlankPdfWindow, renderPdfBytesInWindow } from '@/utils/pdfBytesWindow'
import { renderTestRequisitionPDFFromCanonical } from '@/utils/PDFRenderer'
import { clientTrfPdfFilename } from '@/utils/trfPdfFilename'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

const CompletedRequestForm: React.FC = () => {
  const location = useLocation()
  // `location.state` may be the raw Epic OAuth state string, or a navigation payload
  // object that carries it as `epicState`. Extract the string form so a non-Epic router
  // payload (or a stale object) cannot be mistaken for an EHR submit.
  const rawState = location.state as string | { epicState?: string } | null | undefined
  const epicState =
    typeof rawState === 'string' ? rawState : typeof rawState?.epicState === 'string' ? rawState.epicState : ''
  const state = epicState
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const { config } = useUiConfig()
  const { mode: launchMode } = useLaunchMode()
  // Treat a present Epic OAuth state as authoritative for the EHR flow, falling back to
  // the current browser launch mode. Only the extracted `epicState` string counts here —
  // an arbitrary truthy router payload must not force EHR Section C/D logic.
  const isEhrOrder = Boolean(epicState) || launchMode === 'epic_smart'
  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const handleGeneratePDF = async () => {
    return Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Generate PDF from Completed Form',
      },
      async (span) => {
        span.setAttribute('form.testType', storedCanonical?.order?.selectedOption ?? '')
        setIsGeneratingPDF(true)
        setPdfError(null)

        // The tab is claimed in the click's own tick: opening it after the PDF
        // has been generated would make it an unsolicited popup.
        const pdfWindow = openBlankPdfWindow()
        if (!pdfWindow) {
          span.setAttribute('error', true)
          span.setAttribute('error.message', 'Popup blocked')
          Sentry.captureException(new Error('Popup blocked. Please allow popups for this site.'), {
            tags: { operation: 'ui.action.pdf_open' },
          })
          setPdfError('Popups are blocked for this site. Allow them to open the requisition PDF.')
          setIsGeneratingPDF(false)
          return
        }

        try {
          const orderFormData = await extractFormOrderData(state)
          if (!storedCanonical) throw new Error('No canonical order data available for PDF generation.')
          // This route has no server-rendered equivalent: it holds no order or session id.
          warnClientSideTrfRender({ reason: 'completed_form_route', renderer: 'canonical' })
          const pdfBytes = await renderTestRequisitionPDFFromCanonical(
            storedCanonical,
            orderFormData,
            getTrfPdfConfigFields(config),
            isEhrOrder
          )
          span.setAttribute('pdf.size', pdfBytes.length)

          // Paint the PDF into the spawned tab as canvases with our own
          // Download/Print controls. Epic's embedded browser has no PDF plugin,
          // so a native viewer (blob/data URI/served .pdf) shows a blank tab —
          // painting the pixels ourselves is the only thing that displays.
          await renderPdfBytesInWindow(pdfWindow, pdfBytes, { filename: clientTrfPdfFilename() })
        } catch (error) {
          pdfWindow.close()
          span.setAttribute('error', true)
          if (error instanceof Error) {
            span.setAttribute('error.message', error.message)
          }
          logger.error('Completed form PDF generation failed', {
            error: error instanceof Error ? error.message : String(error),
          })
          Sentry.captureException(error, { tags: { operation: 'ui.action.pdf_generation' } })
          setPdfError(error instanceof Error ? error.message : 'Failed to generate PDF')
        } finally {
          setIsGeneratingPDF(false)
        }
      }
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Form Complete!</h1>
          <p className="mt-2 text-lg text-gray-600">Your requisition form has been successfully completed.</p>
        </div>

        {/* Actions */}
        <div className="rounded-lg bg-white shadow">
          <div className="px-6 py-8">
            {pdfError && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-1 text-sm text-red-700">{pdfError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <button
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF}
                className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="mr-2 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Open PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Simple next steps */}
        <div className="mt-6 rounded-lg bg-blue-50 p-4">
          <p className="text-sm text-blue-800">
            <strong>Next:</strong> Download your PDF, print it, have it signed, and submit according to your
            institution's procedures.
          </p>
        </div>
      </div>
    </div>
  )
}

export default CompletedRequestForm
